import { getCollisionStateConfig, isKnownCollisionState } from '@/components/collision/collisionStateConfig';
import type { ProjectModel } from '@/types/project.types';
import { ShieldOff } from 'lucide-react';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

type CollisionProps = Pick<ProjectModel, 'collision_project_id' | 'collision_simulation_state' | 'collision_url'>;

interface Props {
  project: CollisionProps;
}

export const CollisionDetectionPanel: FC<Props> = ({ project }) => {
  const { t } = useTranslation();

  const hasCollisionData = !!(project.collision_project_id ?? project.collision_simulation_state);
  const state = project.collision_simulation_state;
  const canOpen = !!(project.collision_url && state && state !== 'running');

  if (!hasCollisionData) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="h-1 bg-gray-200" />
        <div className="px-5 py-4 flex items-center gap-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center">
            <ShieldOff className="w-5 h-5 text-gray-400" />
          </div>
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">
              {t('projectDetail.collisionDetection', 'Collision Detection')}
            </p>
            <Tooltip target=".collision-unavailable-hint" position="right" />
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gray-300 flex-shrink-0" />
              <span
                className="collision-unavailable-hint text-sm font-medium text-gray-400 italic cursor-help"
                data-pr-tooltip={t(
                  'projectDetail.collisionUnavailableTooltip',
                  'Collision detection requires both a target model and a blank model to be uploaded.'
                )}
              >
                {t('projectDetail.collisionUnavailable', 'unavailable')}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cfg = getCollisionStateConfig(state);
  const Icon = cfg.icon;
  const label = isKnownCollisionState(state) ? t(`projectDetail.collisionStates.${state}`, state) : (state ?? '—');

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className={`h-1 bg-gradient-to-r ${cfg.accentClass}`} />
      <div className="px-5 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`flex-shrink-0 w-11 h-11 rounded-full ${cfg.iconBg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${cfg.iconText} ${state === 'running' ? 'animate-spin' : ''}`} />
          </div>
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">
              {t('projectDetail.collisionDetection', 'Collision Detection')}
            </p>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
              <span className={`text-sm font-semibold ${cfg.text}`}>{label}</span>
            </div>
          </div>
        </div>

        {canOpen && (
          <a href={project.collision_url!} target="_blank" rel="noopener noreferrer">
            <Button
              label={t('projectDetail.openInCollisions', 'Open in Collisions')}
              size="small"
              tabIndex={-1}
            />
          </a>
        )}
      </div>
    </div>
  );
};
